import { useCallback, useEffect, useState } from "react";
import { Download, Film, Loader2, Check, Globe } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, BACKEND, errMsg } from "@/lib/api";

const mb = (b) => (b ? `${(b / 1048576).toFixed(1)} MB` : "");
const fmt = (iso) => new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const RenderRow = ({ r, isHero, onPublish }) => (
  <li data-testid={`render-row-${r.id}`} className="flex flex-wrap items-center gap-3 py-4">
    <div className="mr-auto">
      <p className="text-sm text-[#1C1715]">{fmt(r.created_at)} · {r.filled_slots}/12 fotos</p>
      <p className="text-xs text-[#8C827A]">
        {r.status === "done" && `4K ${mb(r.size_4k)} · web ${mb(r.size_web)}`}
        {r.status === "failed" && <span className="text-red-700">Error: {r.error}</span>}
        {r.status === "rendering" && `Generando… ${Math.round(r.progress * 100)}%`}
      </p>
    </div>
    {r.status === "done" && (
      <>
        <a data-testid={`render-download-4k-${r.id}`} href={`${BACKEND}/api/renders/${r.id}/download?quality=4k`} className="inline-flex items-center gap-1.5 rounded-full border border-[#1C1715]/15 px-3 py-1.5 text-xs hover:border-[#1C1715] transition-colors"><Download className="h-3.5 w-3.5" /> 4K</a>
        <a data-testid={`render-download-web-${r.id}`} href={`${BACKEND}/api/renders/${r.id}/download?quality=web`} className="inline-flex items-center gap-1.5 rounded-full border border-[#1C1715]/15 px-3 py-1.5 text-xs hover:border-[#1C1715] transition-colors"><Download className="h-3.5 w-3.5" /> Web</a>
        {isHero ? (
          <span data-testid={`render-hero-badge-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#C5A059]/20 px-3 py-1.5 text-xs text-[#7a5a2e]"><Check className="h-3.5 w-3.5" /> En portada</span>
        ) : (
          <button data-testid={`render-publish-${r.id}`} onClick={() => onPublish(r.id)} className="inline-flex items-center gap-1.5 rounded-full bg-[#1C1715] px-3 py-1.5 text-xs text-[#FDFBF7] hover:bg-[#A67C52] transition-colors"><Globe className="h-3.5 w-3.5" /> Usar en portada</button>
        )}
      </>
    )}
  </li>
);

export const RenderPanel = ({ photos }) => {
  const [data, setData] = useState({ renders: [], hero_render_id: null });
  const load = useCallback(() => api.get("/renders").then((r) => setData(r.data)), []);
  const running = data.renders.find((r) => r.status === "rendering");

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [running, load]);

  const start = async () => {
    try { await api.post("/renders"); toast.success("Generando el vídeo 4K…"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const publish = async (id) => {
    try { await api.post(`/renders/${id}/publish`); toast.success("Vídeo publicado en la portada"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section data-testid="render-panel" className="border border-[#1C1715]/10 bg-white p-6 sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">Vídeo de portada</p>
          <p className="mt-2 text-sm text-[#5C534C]">12 s · 3840 × 2160 · en bucle · sin sonido · <span data-testid="photo-count">{photos.length}/12 fotos</span></p>
        </div>
        <Button data-testid="generate-video-button" onClick={start} disabled={!!running || photos.length === 0} className="rounded-full px-7 uppercase tracking-[0.18em] text-xs">
          {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Film className="mr-2 h-4 w-4" />}
          {running ? "Generando…" : "Generar vídeo"}
        </Button>
      </div>
      {running && <Progress data-testid="render-progress" value={running.progress * 100} className="mt-6 h-1" />}
      {data.renders.length > 0 && (
        <ul className="mt-6 divide-y divide-[#1C1715]/10 border-t border-[#1C1715]/10">
          {data.renders.map((r) => <RenderRow key={r.id} r={r} isHero={r.id === data.hero_render_id} onPublish={publish} />)}
        </ul>
      )}
    </section>
  );
};
